import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { debounce } from '../../utils/nodash';
import * as styles from './truncated-review-text.style';

const MAX_LINES = 2;
const RESIZE_DEBOUNCE_MS = 150;

export type TruncatedReviewTextProps = {
  description: string;
};

const TruncatedReviewText = ({
  description,
}: TruncatedReviewTextProps): JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const [displayText, setDisplayText] = useState(description);
  const [isClamped, setIsClamped] = useState(false);
  const [hasMeasured, setHasMeasured] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const measureTextRef = useRef<HTMLSpanElement>(null);
  const measureSuffixRef = useRef<HTMLSpanElement>(null);
  // A platinum profile can render up to 50 cards at once - only measure
  // (reflow-heavy) cards once they're near the viewport, rather than all
  // of them in the same pass, so expanding the list doesn't block the
  // main thread with every card's binary search at once.
  const { ref: inViewRef, inView } = useInView({
    triggerOnce: true,
    rootMargin: '200px 0px',
  });
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  useEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    const measureText = measureTextRef.current;
    const measureSuffix = measureSuffixRef.current;
    if (!inView || !container || !measure || !measureText || !measureSuffix) {
      return undefined;
    }

    // ResizeObserver also fires on height-only changes (its initial callback,
    // expanding/collapsing) - truncation only depends on width, so skip the
    // reflow-heavy re-measure unless the width actually changed.
    let lastWidth = 0;
    const compute = () => {
      const width = container.getBoundingClientRect().width;
      if (width === 0 || width === lastWidth) {
        return;
      }
      lastWidth = width;
      measure.style.width = `${width}px`;
      const lineHeight = parseFloat(getComputedStyle(measure).lineHeight);
      const maxHeight = lineHeight * MAX_LINES;

      measureText.textContent = description;
      measureSuffix.textContent = '';
      if (measure.scrollHeight <= maxHeight + 1) {
        setDisplayText(description);
        setIsClamped(false);
        setExpanded(false);
        setHasMeasured(true);
        measureText.textContent = '';
        return;
      }

      let lo = 0;
      let hi = description.length;
      while (lo < hi - 1) {
        const mid = Math.floor((lo + hi) / 2);
        measureText.textContent = `${description.slice(0, mid)}... `;
        measureSuffix.textContent = 'Read more';
        if (measure.scrollHeight <= maxHeight + 1) {
          lo = mid;
        } else {
          hi = mid;
        }
      }
      setDisplayText(description.slice(0, lo));
      setIsClamped(true);
      setHasMeasured(true);
      // Measurement is done - don't leave a near-duplicate of the review
      // text sitting in a hidden node.
      measureText.textContent = '';
      measureSuffix.textContent = '';
    };

    compute();
    const [debouncedCompute, cancelDebouncedCompute] = debounce(
      compute,
      RESIZE_DEBOUNCE_MS,
    );
    const resizeObserver = new ResizeObserver(() => debouncedCompute());
    resizeObserver.observe(container);
    return () => {
      cancelDebouncedCompute();
      resizeObserver.disconnect();
    };
  }, [description, inView]);

  return (
    <>
      <div
        ref={measureRef}
        aria-hidden
        data-testid="truncated-review-measure"
        css={styles.measure}
      >
        <span ref={measureTextRef} />
        <span ref={measureSuffixRef} css={styles.measureSuffix} />
      </div>
      <div ref={setContainerRef}>
        <p
          data-testid="review-description"
          css={hasMeasured || expanded ? styles.body : styles.clampedBody}
        >
          {expanded || !isClamped ? description : `${displayText}... `}
          {hasMeasured && isClamped && (
            <button
              type="button"
              css={expanded ? styles.showLessButton : styles.readMoreButton}
              aria-expanded={expanded}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </p>
      </div>
    </>
  );
};

export default TruncatedReviewText;
